import { useState, useEffect, useContext, forwardRef, useMemo } from 'react'
import { Toolbar, Fab, Grid, Dialog, Slide, Skeleton, LinearProgress, CircularProgress } from '@mui/material'
import { Close } from '@mui/icons-material'
import { getUserNameById, styleModalReport, dateWithTime } from '../config'
import { reportsRoutes, usersRoutes, pdfMakeRoutes, executionReportsRoutes } from '../routes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft, faArrowUp, faPen, faTrash, faFilePdf, faEye } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import { AuthContext, useExecutionReportContext, useReportsContext, useUser, useSitesContext, useUsersContext, useAlertContext } from '../context'
import { reportsDatabase } from '../indexedDB'
import { dateV2 } from '../utils/DateUtils'
import { calculateHours, calculateDays, getHistoryDate } from '../utils/timeCalculation'
import PDFFileDialog from './PDFfileDialog'
import ReportConfirmDialog from './ReportConfirm.dialog'
import ReportItemsPreviewDialog from './ReportItemsPreviewDialog'
import { useStatus } from '../context/Status.context'

const Transition = forwardRef(function Transition (props, ref) {
    return <Slide direction="left" ref={ref} {...props} />
})

const ReviewReportDialog = ({ open, report, onlyClose }) => {
    const { isSuperAdmin, admin, isSapExecutive, isOperatorInspector, isOperator, isShiftManager, isChiefMachinery } = useUser()
    const { getReports, getReportsOffline, priorityAssignments, setReport, updateLocalReport } = useReportsContext()
    const { setAlertSimple, setConfirm } = useAlertContext()

    const { usersMap } = useUsersContext()
    const [colorState, setColorState] = useState()
    const { getExecutionReport } = useExecutionReportContext()
    const { idIndex, guide, state, siteName, usersAssigned, sapId, maquina, description, obra, historialAsignaciones, progress } = report
    const [userAssignedName, setUserAssignedName] = useState()
    const [userShiftManagerName, setUserShiftManagerName] = useState()
    const [userChiefMachineryName, setChiefMachineryName] = useState()
    const [userSapExecutiveName, setUserSapExecutiveName] = useState()
    const [userOperatorFinishedName, setUserOperatorFinishedName] = useState()
    const [resolvedShiftManagerDate, setResolvedShiftManagerDate] = useState(null)
    const [resolvedChiefMachineryDate, setResolvedChiefMachineryDate] = useState(null)
    const [resolvedDateClose, setResolvedDateClose] = useState(null)
    const [loadingDelete, setLoadingDelete] = useState(false)
    const [toEditSapId, setToEditSapId] = useState(false)
    const [sapIdToEdit, setSapIdToEdit] = useState(sapId)
    const [isSavingSapId, setIsSavingSapId] = useState(false)
    const [tiempoEnProcesoOperador, setTiempoEnProcesoOperador] = useState(0)
    const [tiempoAprobacionSup, setTiempoAprobacionSup] = useState(0)
    const [tiempoJMaquinaria, setTiempoJMaquinaria] = useState(0)
    const [tiempoCierreOt, setTiempoCierreOt] = useState(0)
    const [tiempoTotal, setTiempoTotal] = useState(0)
    const [promedioOperadores, setPromedioOperadores] = useState(0)
    const [historialConOperadores, setHistorialConOperadores] = useState([])
    const [mostrarHistorial, setMostrarHistorial] = useState(false)
    const [mostrarHorasOperadores, setMostrarHorasOperadores] = useState(false)
    const [mostrarHistorialAsignaciones, setMostrarHistorialAsignaciones] = useState(false)
    const [historialAsignacionesCache, setHistorialAsignacionesCache] = useState([])
    const [executionProgress, setExecutionProgress] = useState(null)
    const [mostrarDetalleEjecucion, setMostrarDetalleEjecucion] = useState(false)
    const [showConflictDialog, setShowConflictDialog] = useState(false)
    const [conflictingReport, setConflictingReport] = useState(null)
    const [tempSapId, setTempSapId] = useState('')
    const { initiatePdfGeneration, status, setstatus } = useStatus()
    const navigate = useNavigate()

    const handleGeneratePdf = async () => {
        setIsGeneratingPdf(true)
        try {
            await initiatePdfGeneration(report)
        } catch (error) {
            console.error('Error generando PDF:', error)
        } finally {
            setIsGeneratingPdf(false)
        }
    }

    const effectiveHoursData = useMemo(() => {
        if (!report || !report.ejecuciones) return { horasPorOperador: [], totalHorasOperadores: 0 }

        const hoursByUser = {}
        report.ejecuciones.forEach(exec => {
            if (exec.inicio && exec.termino && exec.userId) {
                const diff = (new Date(exec.termino) - new Date(exec.inicio)) / (1000 * 60 * 60)
                if (!hoursByUser[exec.userId]) {
                    hoursByUser[exec.userId] = 0
                }
                hoursByUser[exec.userId] += diff
            }
        })

        const horasPorOperador = Object.keys(hoursByUser).map(userId => {
            const user = usersMap[userId]
            return {
                nombre: user ? `${user.name} ${user.lastName}` : 'Desconocido',
                horas: parseFloat(hoursByUser[userId].toFixed(1))
            }
        })

        const totalHorasOperadores = parseFloat(horasPorOperador.reduce((sum, h) => sum + h.horas, 0).toFixed(1))
        return { horasPorOperador, totalHorasOperadores }
    }, [report, usersMap])

    const totalBacklogs = report.hallazgosCount || 0
    const totalNoChecks = report.noChecksCount || 0

    const getUsers = async () => {
        if (usersAssigned && usersAssigned.length > 0) {
            const promises = usersAssigned.map(async (u) => {
                if (u && typeof u === 'object' && u.name) {
                    return `${u.name} ${u.lastName}`
                }
                if (!u || u === 'null') return null
                try {
                    const user = usersMap[typeof u === 'string' ? u : u._id] || (await usersRoutes.getUser(typeof u === 'string' ? u : u._id))?.data
                    if (user) {
                        return user.name + ' ' + user.lastName
                    }
                } catch (error) {
                    console.error('Error fetching user', error)
                }
                return null
            })
            const results = await Promise.all(promises)
            setUserAssignedName(results.filter(n => n))
            return
        }

        if (historialAsignaciones && historialAsignaciones.length > 0) {
            const sortedHistory = [...historialAsignaciones].sort((a, b) => a.id - b.id)
            const firstAssignee = sortedHistory[0].asignadoA

            if (firstAssignee && typeof firstAssignee === 'object' && firstAssignee.name) {
                setUserAssignedName([`${firstAssignee.name} ${firstAssignee.lastName}`])
            } else if (firstAssignee && firstAssignee !== 'null') {
                const id = typeof firstAssignee === 'string' ? firstAssignee : firstAssignee._id
                const user = usersMap[id]
                if (user) {
                    setUserAssignedName([`${user.name} ${user.lastName}`])
                } else {
                    const name = await getUserNameById(firstAssignee)
                    setUserAssignedName([name])
                }
            } else {
                setUserAssignedName([])
            }
        } else {
            setUserAssignedName([])
        }
    }

    useEffect(() => {
        const processHistorial = async () => {
            if (historialAsignaciones && historialAsignaciones.length > 0) {
                // 1. Recolectar IDs únicos
                const uniqueIds = new Set()
                historialAsignaciones.forEach(h => {
                    const asignadoPorId = h.asignadoPor && h.asignadoPor._id ? h.asignadoPor._id : (h.asignadoPor && typeof h.asignadoPor === 'string' ? h.asignadoPor : null)
                    const asignadoAId = h.asignadoA && h.asignadoA._id ? h.asignadoA._id : (h.asignadoA && typeof h.asignadoA === 'string' ? h.asignadoA : null)
                    if (asignadoPorId && asignadoPorId !== 'null') uniqueIds.add(asignadoPorId)
                    if (asignadoAId && asignadoAId !== 'null') uniqueIds.add(asignadoAId)
                })

                // 2. Resolver nombres usando usersMap o fallback a getUserNameById
                const namesMap = {}
                const promises = Array.from(uniqueIds).map(async (id) => {
                    if (usersMap[id]) {
                        namesMap[id] = `${usersMap[id].name} ${usersMap[id].lastName}`
                    } else {
                        const name = await getUserNameById(id)
                        namesMap[id] = name
                    }
                })
                await Promise.all(promises)

                // 3. Construir historial usando el mapa
                const sortedHistory = [...historialAsignaciones].sort((a, b) => (a.id || 0) - (b.id || 0))

                const processedHistory = sortedHistory.map(h => {
                    let asignadoPor = 'Sin Información'
                    if (h.asignadoPor && h.asignadoPor.name) {
                        asignadoPor = `${h.asignadoPor.name} ${h.asignadoPor.lastName}`
                    } else {
                        const id = h.asignadoPor && h.asignadoPor._id ? h.asignadoPor._id : h.asignadoPor
                        if (id) asignadoPor = namesMap[id] || 'Sin Información'
                    }

                    let asignadoA = 'Sin Información'
                    if (h.asignadoA && h.asignadoA.name) {
                        asignadoA = `${h.asignadoA.name} ${h.asignadoA.lastName}`
                    } else {
                        const id = h.asignadoA && h.asignadoA._id ? h.asignadoA._id : h.asignadoA
                        if (id) asignadoA = namesMap[id] || 'Sin Información'
                    }

                    let fechaRaw = h.fecha
                    if (!fechaRaw && h._id) {
                        try {
                            const objectIdStr = typeof h._id === 'string' ? h._id : (h._id.$oid || h._id.toString())
                            if (objectIdStr && objectIdStr.length === 24) {
                                const timestamp = parseInt(objectIdStr.substring(0, 8), 16) * 1000
                                fechaRaw = new Date(timestamp)
                            }
                        } catch (e) {
                            console.error('Error recovering date from ObjectId:', e)
                        }
                    }

                    const fecha = dateV2(fechaRaw || h.id, 'dd/mm/yyyy hh:mm')

                    return {
                        asignadoPor,
                        asignadoA,
                        fecha
                    }
                })

                setHistorialAsignacionesCache(processedHistory)
            }
        }
        processHistorial()
    }, [historialAsignaciones, usersMap])

    useEffect(() => {
        init()
        if (report) {
            console.log('Report data with execution:', report)

            const dateInit = Date.parse(report.dateInit)
            const endReport = Date.parse(report.endReport)

            const shiftManagerDate = Date.parse(report.shiftManagerApprovedDate) || getHistoryDate(report, 'aprobacion-supervisor')
            const chiefMachineryDate = Date.parse(report.chiefMachineryApprovedDate) || getHistoryDate(report, 'aprobacion-jefe-maquinaria')

            let dateClose = Date.parse(report.dateClose || report.sapExecutiveApprovedDate) || getHistoryDate(report, 'aprobado-sap')

            if (!dateClose && report.state === 'Completadas') {
                dateClose = Date.parse(report.updatedAt)
            }

            setResolvedShiftManagerDate(shiftManagerDate)
            setResolvedChiefMachineryDate(chiefMachineryDate)
            setResolvedDateClose(dateClose)
            setTiempoEnProcesoOperador(effectiveHoursData.totalHorasOperadores)

            const tSup = (!shiftManagerDate || isNaN(shiftManagerDate))
                ? NaN
                : calculateHours(endReport, shiftManagerDate)
            setTiempoAprobacionSup(tSup)

            const lastBeforeMach = shiftManagerDate || endReport
            const tMach = (!chiefMachineryDate || isNaN(chiefMachineryDate))
                ? NaN
                : calculateHours(lastBeforeMach, chiefMachineryDate)
            setTiempoJMaquinaria(tMach)

            const lastBeforeClose = chiefMachineryDate || shiftManagerDate || endReport
            const tClose = (!dateClose || isNaN(dateClose))
                ? NaN
                : calculateHours(lastBeforeClose, dateClose)
            setTiempoCierreOt(tClose)

            let tTotal = NaN
            if (dateInit && !isNaN(dateInit)) {
                const finalDate = dateClose || chiefMachineryDate || shiftManagerDate || endReport
                if (finalDate && !isNaN(finalDate)) {
                    tTotal = calculateHours(dateInit, finalDate)
                }
            }
            setTiempoTotal(tTotal)

            const processReportHistory = async () => {
                let operadores = 0
                const historial = []
                if (report.history && report.history.length > 0) {
                    const historyResults = report.history.map((item) => {
                        if (item.operadores) {
                            let usuarioData = { name: 'Desconocido', lastName: '' }
                            if (item.userSendingData && item.userSendingData !== 'null') {
                                const user = usersMap[item.userSendingData]
                                if (user) {
                                    usuarioData = user
                                } else if (item.userName) {
                                    usuarioData = { name: item.userName, lastName: '' }
                                }
                            }
                            return { ...item, usuario: usuarioData }
                        }
                        return null
                    })

                    const validResults = historyResults.filter(r => r !== null)

                    validResults.forEach(item => {
                        historial.push(item)
                        operadores += Number(item.operadores || 0)
                    })

                    const promedioOperadoresCache = historial.length > 0 ? operadores / historial.length : 0
                    setPromedioOperadores(promedioOperadoresCache)
                    setHistorialConOperadores(historial)
                }
            }
            processReportHistory()
        }
    }, [report, usersMap])

    const secondsToString = (seconds) => {
        let hour = Math.floor(seconds / 3600)
        hour = (hour < 10) ? '0' + hour : hour
        let minute = Math.floor((seconds / 60) % 60)
        minute = (minute < 10) ? '0' + minute : minute
        let second = seconds % 60
        second = (second < 10) ? '0' + second : second
        return hour + ':' + minute /* + ':' + second */
    }

    const init = async () => {
        const fetchName = async (data, historyType, setter) => {
            if (data) {
                if (typeof data === 'object' && data.name) {
                    setter(`${data.name} ${data.lastName}`)
                    return
                } else if (typeof data === 'string' && data !== 'null') {
                    const user = usersMap[data]
                    if (user) {
                        setter(`${user.name} ${user.lastName}`)
                        return
                    } else {
                        const name = await getUserNameById(data)
                        setter(name)
                        return
                    }
                }
            }

            const historyDate = getHistoryDate(report, historyType)
            if (historyDate) {
                const historyItem = report.history?.find(h => h.type === historyType)
                if (historyItem) {
                    if (historyItem.userName) {
                        setter(historyItem.userName)
                    } else if (historyItem.userSendingData) {
                        const user = usersMap[historyItem.userSendingData]
                        if (user) {
                            setter(`${user.name} ${user.lastName}`)
                        } else {
                            const name = await getUserNameById(historyItem.userSendingData)
                            setter(name)
                        }
                    }
                }
            } else {
                setter(null)
            }
        }

        await Promise.all([
            fetchName(report.chiefMachineryApprovedBy, 'aprobacion-jefe-maquinaria', setChiefMachineryName),
            fetchName(report.shiftManagerApprovedBy, 'aprobacion-supervisor', setUserShiftManagerName),
            fetchName(report.sapExecutiveApprovedBy, 'aprobado-sap', setUserSapExecutiveName),
            fetchName(report.operatorFinishedBy, 'cierre-completo', setUserOperatorFinishedName)
        ])

        getUsers()

        if (state === 'Asignar') {
            setColorState('#DE4343')
        } else if (state === 'En proceso') {
            setColorState('#F2994A')
        } else if (state === 'Por cerrar') {
            setColorState('#F2C94C')
        } else if (state === 'Completadas') {
            setColorState('#27AE60')
        }
    }

    const toReport = async () => {
        console.log('[ReviewReportDialog] Navegando a revisión para OT:', report.newIdIndex || report.idIndex)
        setReport(report)

        const isAssigned = priorityAssignments.some(a => String(a._id) === String(report._id))
        const idToNavigate = report.newIdIndex ? report.newIdIndex : report.idIndex
        let finalExecutionReport = null

        const hasPermission = isOperator || admin || isSuperAdmin || isShiftManager || isChiefMachinery || isOperatorInspector

        if (isAssigned && hasPermission) {
            try {
                console.log(`[ReviewReportDialog] Reporte asignado. Llamando a startExecutionReport para: ${report._id}`)
                const response = await executionReportsRoutes.startExecutionReport(report._id)
                finalExecutionReport = response.data
                updateLocalReport(report._id, { dateInit: new Date() })
            } catch (error) {
                console.error('[ReviewReportDialog] Error al iniciar/recuperar execution report:', error)
                if (error.response && error.response.status === 403) {
                    setAlertSimple('Acceso Denegado', 'No tiene permisos para iniciar o acceder a este reporte.')
                    return
                }
            }
        }

        console.log('[ReviewReportDialog] Navegando a /assignment/' + idToNavigate)
        navigate(
            `/assignment/${idToNavigate}`,
            { state: { executionReport: finalExecutionReport, from: 'reports' } }
        )
    }

    const removeDuplicates = (arr) => {
        const unique = []
        for (let i = 0; i < arr.length; i++) {
            if (unique.indexOf(arr[i]) === -1) {
                unique.push(arr[i])
            }
        }
        return unique
    }

    const handleSoftDeleteOt = async () => {
        setConfirm(
            'Confirmar Eliminación',
            `Se marcará la OT ${report.idIndex} como eliminada lógicamente. Podrá ser recuperada por un administrador. ¿Desea continuar?`,
            async () => {
                setLoadingDelete(true)
                try {
                    const response = await reportsRoutes.deleteReport(report._id)
                    console.log(response.data)
                    const { database } = await reportsDatabase.initDbReports()
                    await reportsDatabase.eliminar(report.idIndex, database)
                    getReportsOffline()
                    getReports()
                    setstatus({
                        status: 'completed',
                        severity: 'success',
                        title: 'Eliminación Exitosa',
                        message: `OT ${report.idIndex} marcada como eliminada exitosamente.`
                    })
                    onlyClose()
                } catch (error) {
                    console.error('Error al realizar el borrado lógico de la OT:', error)
                    setstatus({
                        status: 'error',
                        severity: 'error',
                        title: 'Error de Eliminación',
                        message: 'Hubo un error al intentar eliminar la OT lógicamente. Por favor, intente de nuevo.'
                    })
                } finally {
                    setLoadingDelete(false)
                }
            }
        )
    }

    const changeInputSapId = () => {
        if (!toEditSapId) {
            setToEditSapId(true)
        } else {
            setToEditSapId(false)
        }
    }

    const saveChangesSapId = async () => {
        if (sapIdToEdit.length < 1) {
            setAlertSimple('Campo Requerido', 'Debe agregar algún número SAP')
        } else {
            setIsSavingSapId(true)
            try {
                const reportToEdit = {
                    _id: report._id,
                    sapId: sapIdToEdit
                }
                const response = await reportsRoutes.editReport(reportToEdit)
                console.log(response)
                if (response) {
                    report.sapId = sapIdToEdit
                    report.urlPdf = null
                    changeInputSapId()
                    getReportsOffline()
                    getReports()
                    setstatus({
                        status: 'completed',
                        severity: 'success',
                        title: 'Actualización Exitosa',
                        message: `ID SAP actualizado exitosamente a ${sapIdToEdit}.`
                    })
                }
            } catch (error) {
                console.error('Error al guardar el ID SAP:', error)
                if (error.response?.status === 409) {
                    const existingReport = error.response.data?.existingReport || error.response.data?.message?.existingReport
                    setConflictingReport(existingReport)
                    setTempSapId(sapIdToEdit)
                    setShowConflictDialog(true)
                } else {
                    setstatus({
                        status: 'error',
                        severity: 'error',
                        title: 'Error al actualizar SAP ID',
                        message: error.response?.data?.message || error.message || 'Ocurrió un error inesperado al intentar actualizar el ID SAP.'
                    })
                }
            } finally {
                setIsSavingSapId(false)
            }
        }
    }

    const handleForceUpdateSapId = async () => {
        setIsSavingSapId(true)
        try {
            const reportToEdit = {
                _id: report._id,
                sapId: tempSapId,
                force: true
            }
            const response = await reportsRoutes.editReport(reportToEdit)
            console.log(response)
            if (response) {
                report.sapId = tempSapId
                report.urlPdf = null
                setShowConflictDialog(false)
                changeInputSapId()
                getReportsOffline()
                getReports()
                setstatus({
                    status: 'completed',
                    severity: 'success',
                    title: 'Actualización Exitosa',
                    message: `ID SAP actualizado exitosamente a ${tempSapId}.`
                })
            }
        } catch (error) {
            console.error('Error al forzar el ID SAP:', error)
            setShowConflictDialog(false)
            setstatus({
                status: 'error',
                severity: 'error',
                title: 'Error al forzar actualización',
                message: error.response?.data?.message || error.message || 'Error al intentar anular el reporte conflictivo y forzar la actualización.'
            })
        } finally {
            setIsSavingSapId(false)
        }
    }

    const [openPdfDialog, setOpenPdfDialog] = useState(false)
    const [fileSelected, setFileSelected] = useState(null)
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
    const [isGeneratingNoChecksPdf, setIsGeneratingNoChecksPdf] = useState(false)
    const [downloadFileName, setDownloadFileName] = useState(null)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewType, setPreviewType] = useState('findings')
    const [executionReport, setExecutionReport] = useState(null)
    const [loadingExecution, setLoadingExecution] = useState(false)

    useEffect(() => {
        if (open && report && report._id) {
            const fetchExecution = async () => {
                setLoadingExecution(true)
                try {
                    const response = await executionReportsRoutes.getExecutionByReportId(report._id)
                    setExecutionReport(response.data)
                } catch (err) {
                    console.error('Error pre-loading execution report:', err)
                } finally {
                    setLoadingExecution(false)
                }
            }
            fetchExecution()
        } else {
            setExecutionReport(null)
        }
    }, [open, report])

    const handleGenerateSummaryPdf = async () => {
        setIsGeneratingPdf(true)
        try {
            const summaryReport = { ...report, isSummary: true }
            await initiatePdfGeneration(summaryReport)
        } catch (error) {
            console.error('Error generating summary PDF', error)
            setstatus({
                status: 'error',
                message: 'Ocurrió un error al iniciar la generación del PDF resumen.',
                errorDetails: { code: error.code || 'N/A', info: error.message || 'Error desconocido' },
                isSummary: true
            })
        } finally {
            setIsGeneratingPdf(false)
        }
    }

    const handleGenerateNoChecksPdf = async () => {
        setIsGeneratingNoChecksPdf(true)
        try {
            const noChecksReport = { ...report, isNoChecks: true }
            await initiatePdfGeneration(noChecksReport)
        } catch (error) {
            console.error('Error generating no-checks PDF', error)
            setstatus({
                status: 'error',
                message: 'Ocurrió un error al iniciar la generación del PDF de tareas no ejecutadas.',
                errorDetails: { code: error.code || 'N/A', info: error.message || 'Error desconocido' },
                isNoChecks: true
            })
        } finally {
            setIsGeneratingNoChecksPdf(false)
        }
    }
    console.log('Renderizando ReviewReportDialog con report:', report)

    return (
        <Dialog
            open={open}
            onClose={onlyClose}
            TransitionComponent={Transition}
            adaptiveHeight={true}
            maxWidth={'xl'}
        >
            <div style={{ padding: 30, width: 1000 }}>
                <PDFFileDialog
                    open={openPdfDialog}
                    handleClose={() => { setOpenPdfDialog(false); setFileSelected(null); setDownloadFileName(null) }}
                    file={fileSelected}
                    downloadFileName={downloadFileName}
                />
                <ReportConfirmDialog
                    open={showConflictDialog}
                    handleClose={() => { setShowConflictDialog(false); setConflictingReport(null) }}
                    handleConfirm={handleForceUpdateSapId}
                    existingReport={conflictingReport}
                    currentReport={{ ...report, sapId: tempSapId }}
                    mode="edit"
                    loading={isSavingSapId}
                />
                <ReportItemsPreviewDialog
                    open={previewOpen}
                    handleClose={() => setPreviewOpen(false)}
                    reportId={report._id}
                    type={previewType}
                    report={report}
                    executionReport={executionReport}
                    loading={loadingExecution}
                    onGenerate={previewType === 'findings' ? handleGenerateSummaryPdf : handleGenerateNoChecksPdf}
                    isGenerating={previewType === 'findings' ? isGeneratingPdf : isGeneratingNoChecksPdf}
                />
                <Toolbar style={{ width: '100%', height: 59, paddingLeft: 0 }}>
                    <h2>{(report.reportType === 'Mantención') ? `Pauta de ${report.reportType}` : ''} Equ: {maquina?.equid?.replace('00000000', '') || 'S/I'} / {maquina?.model || 'S/I'}{/* SAP {sapId ? sapId : 'N/A'} */}</h2>
                    <div style={{ position: 'absolute', right: 65, textAlign: 'center' }}>
                        <div style={{ backgroundColor: colorState, paddingTop: 3, borderRadius: 5, width: 100, height: 20 }}>
                            <p style={{ margin: 0, fontSize: 12 }}>{state.toUpperCase()}</p>
                        </div>
                        {(progress !== undefined && progress !== null) && (
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={Number(progress)}
                                    sx={{
                                        width: 100,
                                        height: 2,
                                        borderRadius: 1,
                                        backgroundColor: '#eee',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: colorState
                                        }
                                    }}
                                />
                                <span style={{ fontSize: 10, marginLeft: 5, fontWeight: 'bold', color: '#666' }}>{Math.round(progress)}%</span>
                            </div>
                        )}
                    </div>
                </Toolbar>
                <Grid container>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                        <strong>Faena:</strong> <br />
                        {obra.descripcion}
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                        <strong>Número de orden:</strong> <br />
                        {report.newIdIndex ? report.newIdIndex : report.idIndex}
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                        <strong>{report.reportType === 'Mantención' ? 'N° SAP:' : 'Aviso SAP:'}</strong> <br />
                        <Grid container alignItems="center">
                            <Grid item>
                                {
                                    !toEditSapId
                                        ? sapIdToEdit || 'N/A'
                                        : <input
                                            onChange={(e) => setSapIdToEdit(e.target.value)}
                                            value={sapIdToEdit}
                                            disabled={isSavingSapId}
                                            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                        />
                                }
                            </Grid>
                            {(isSapExecutive || admin) && <Grid item>
                                {
                                    !(toEditSapId)
                                        ? <button style={{ marginLeft: 10, cursor: 'pointer' }} onClick={changeInputSapId}>
                                            <FontAwesomeIcon icon={faPen} />
                                        </button>
                                        : <button
                                            style={{ marginLeft: 10, cursor: isSavingSapId ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                            onClick={saveChangesSapId}
                                            disabled={isSavingSapId}
                                        >
                                            {isSavingSapId ? (
                                                <CircularProgress size={14} style={{ color: 'inherit' }} />
                                            ) : (
                                                <FontAwesomeIcon icon={faArrowUp} />
                                            )}
                                        </button>
                                }
                            </Grid>}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                        <div style={{ marginTop: 30, width: '100%' }}>
                            <strong>Pauta:</strong> <br />
                            {guide}
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                        <div style={{ marginTop: 30, width: '100%' }}>
                            <strong>Descripción:</strong> <br />
                            {description || 'Sin descripción'}
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>

                        {(totalBacklogs || totalBacklogs === 0)
                            ? <div style={{ marginTop: 30, width: '100%' }}>
                                <p style={{ margin: 0 }}><strong>Hallazgos: </strong>{totalBacklogs > 0 ? totalBacklogs : 'No'}</p>
                                <p style={{ margin: 0, marginTop: 5 }}><strong>Tareas no ejecutadas:</strong> {totalNoChecks > 0 ? totalNoChecks : 'No'}</p>
                            </div>
                            : <div style={{ marginTop: 30, width: '100%' }}>
                                <Skeleton variant="text" sx={{ fontSize: 16, width: 120 }} />
                            </div>
                        }
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item md={6} xs={12} sm={12} lg={4} xl={4} >
                        <div style={{ marginTop: 30, width: '100%' }} className='dataDialog'>
                            <p>Revisores: </p>
                            <p>Pauta asignada a:</p>
                            {
                                !userAssignedName &&
                                <>
                                    <Skeleton variant="text" sx={{ fontSize: 16, width: 120 }} />
                                    <Skeleton variant="text" sx={{ fontSize: 16, width: 120 }} />
                                    <Skeleton variant="text" sx={{ fontSize: 16, width: 120 }} />
                                </>
                            }
                            {
                                userAssignedName && userAssignedName.length > 0
                                    ? removeDuplicates(userAssignedName).map((user, i) => {
                                        return <p key={i}><b>{user}</b></p>
                                    })
                                    : <p style={{ marginTop: 0 }}><b>No encontrado</b></p>
                            }
                            <p>Pauta enviada a revisión por: <br /><b>{userOperatorFinishedName || 'No enviado'}</b></p>
                            <p>Jefe de turno: <br /> <b>{userShiftManagerName || 'No revisado'}</b></p>
                            <p>Jefe de maquinaria: <br /> <b>{userChiefMachineryName || 'No revisado'}</b></p>
                            <p>Ejecutivo SAP: <br /> <b>{userSapExecutiveName || 'No cerrado'}</b></p>
                        </div>
                    </Grid>
                    <Grid item lg={3} xl={3} >
                        <div style={{ marginTop: 30, width: '100%' }} className='dataDialog'>
                            <p>Registro horario: </p>
                            <p>Fecha Creación OT: <br /> <b>{dateV2(report.createdAt, 'full')}</b></p>
                            <p>Fecha Prevista: <br /> <b>{(dateV2(report.datePrev, 'full', false) === 'Sin información') ? 'Faltan datos' : dateV2(report.datePrev, 'full', false)}</b></p>
                            <p>Fecha Inicio: <br /> <b>{(dateV2(report.dateInit, 'full') === 'Sin información') ? 'No inicado' : dateV2(report.dateInit, 'full')}</b></p>
                            <p>Fecha Término: <br /> <b>{(dateV2(report.endReport, 'full') === 'Sin información') ? 'No finalizado' : dateV2(report.endReport, 'full')}</b></p>
                            <p>Aprobado Jefe Turno: <br /> <b>{(dateV2(resolvedShiftManagerDate, 'full') === 'Sin información') ? 'No cerrado' : dateV2(resolvedShiftManagerDate, 'full')}</b></p>
                            <p>Aprobado Jefe maquinaria: <br /> <b>{(dateV2(resolvedChiefMachineryDate, 'full') === 'Sin información') ? 'No cerrado' : dateV2(resolvedChiefMachineryDate, 'full')}</b></p>
                            <p>Cerrado SAP: <br /> <b>{(dateV2(resolvedDateClose, 'full') === 'Sin información') ? 'No cerrado' : dateV2(resolvedDateClose, 'full')}</b></p>
                        </div>
                    </Grid>
                    <Grid item lg={5} xl={5} >
                        <div style={{ marginTop: 30, width: '100%' }} className='dataDialog'>
                            {executionProgress !== null && <p>Progreso de ejecución: <b>{executionProgress}%</b> <button className='buttonGeneric' onClick={() => setMostrarDetalleEjecucion(!mostrarDetalleEjecucion)}>{mostrarDetalleEjecucion ? 'Ocultar' : 'Detalle'}</button></p>}
                            <p>Tiempo ejecución de operadores: <b>{isNaN(tiempoEnProcesoOperador) ? 'S/I' : `${tiempoEnProcesoOperador} hrs`}</b></p>
                            <p>Aprobación de supervisor: <b>{isNaN(tiempoAprobacionSup) ? 'S/I' : `${tiempoAprobacionSup} hrs`}</b></p>
                            <p>Aprobación de jefe maquinaria: <b>{isNaN(tiempoJMaquinaria) ? 'S/I' : `${tiempoJMaquinaria} hrs`}</b></p>
                            <p>Cierre de la OT: <b>{isNaN(tiempoCierreOt) ? 'S/I' : `${tiempoCierreOt} hrs`}</b></p>
                            <p>Tiempo Total: <b>{isNaN(tiempoTotal) || tiempoTotal === 0 ? 'S/I' : `${tiempoTotal} hrs`}</b></p>

                            <p>Promedio de operadores por responsable: <b>{isNaN(promedioOperadores) ? 0 : Math.round(promedioOperadores)}</b> <button className='buttonGeneric' onClick={() => { setMostrarHistorial(!mostrarHistorial) }}>{mostrarHistorial ? 'Ocultar' : 'Detalle'}</button></p>
                            {
                                mostrarHistorial &&

                                <ul style={{ margin: 0 }}>
                                    {
                                        (historialConOperadores.length > 0)
                                            ? historialConOperadores.map((h, i) => {
                                                return <li key={i} style={{ fontSize: 13 }}>
                                                    {`${h.usuario.name} ${h.usuario.lastName}: ${h.operadores} operadores mencionados.`}
                                                </li>
                                            })
                                            : <li style={{ fontSize: 13 }}>
                                                Sin datos
                                            </li>
                                    }
                                </ul>

                            }

                            <p>Horas efectivas por operador:
                                {effectiveHoursData.totalHorasOperadores > 0 && <span> <b>{effectiveHoursData.totalHorasOperadores} hrs </b></span>}
                                <button className='buttonGeneric' onClick={() => { setMostrarHorasOperadores(!mostrarHorasOperadores) }}>
                                    {mostrarHorasOperadores ? 'Ocultar' : 'Detalle'}
                                </button>
                            </p>
                            {
                                mostrarHorasOperadores &&
                                <ul style={{ margin: 0 }}>
                                    {
                                        (effectiveHoursData.horasPorOperador && effectiveHoursData.horasPorOperador.length > 0)
                                            ? effectiveHoursData.horasPorOperador.map((h, i) => (
                                                <li key={i} style={{ fontSize: 13 }}>
                                                    {`${h.nombre}: ${h.horas} hrs`}
                                                </li>
                                            ))
                                            : <li style={{ fontSize: 13 }}>Sin datos</li>
                                    }
                                </ul>
                            }                            <div>
                                <p style={{ margin: 0 }}>
                                    Historial de asignaciones <button style={{ cursor: 'pointer' }} onClick={() => { setMostrarHistorialAsignaciones(!mostrarHistorialAsignaciones) }} >{mostrarHistorialAsignaciones ? 'Ocultar' : 'Detalle'}</button> </p>
                            </div>
                            <div style={{ maxHeight: 150, overflowY: 'auto' }}>

                                {
                                    mostrarHistorialAsignaciones && historialAsignacionesCache.map((h, i) => {
                                        return (
                                            <div style={{ paddingLeft: 10, borderBottom: '1px #ccc solid' }} key={i}>
                                                <p style={{ margin: 0 }}>Asignado por: {h.asignadoPor}</p>
                                                <p style={{ margin: 0 }}>Operario: {h.asignadoA}</p>
                                                <p style={{ margin: 0 }}>Fecha: {h.fecha}</p>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        </div>
                    </Grid>
                </Grid>
                {mostrarDetalleEjecucion && report.execution && report.execution.group && (
                    <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #ccc', padding: 10, marginTop: 10 }}>
                        {Object.entries(report.execution.group).map(([groupName, tasks]) => (
                            <div key={groupName}>
                                <h4 style={{ marginTop: 10, marginBottom: 5 }}>{groupName}</h4>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {tasks.map((task, index) => (
                                        <li key={index} style={{ marginBottom: 5, color: task.isChecked ? 'green' : 'red' }}>
                                            <span style={{ fontWeight: task.isChecked ? 'normal' : 'bold' }}>{task.taskdesc}</span>
                                            {task.isChecked ? ' (Completado)' : ' (Pendiente)'}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
                <Grid container spacing={2}>
                    <Grid item xs>
                        <div style={{ padding: 10 }}>
                            <button style={{ cursor: 'pointer', height: 50, width: '100%', backgroundColor: 'green', color: '#fff', fontSize: 18, borderRadius: 5, border: 'none' }} onClick={toReport}> <strong> <FontAwesomeIcon icon={faArrowCircleLeft} /> Revisar OT {idIndex} </strong> </button>
                        </div>
                    </Grid>

                    {Boolean(report?.endReport || report?.operatorFinishedBy) && (
                        <Grid item xs>
                            <div style={{ padding: 10 }}>
                                <button
                                    disabled={isGeneratingPdf || status?.status === 'generating'}
                                    style={{
                                        cursor: (isGeneratingPdf || status?.status === 'generating') ? 'not-allowed' : 'pointer',
                                        height: 50,
                                        width: '100%',
                                        backgroundColor: (isGeneratingPdf || status?.status === 'generating') ? '#90caf9' : '#1976d2',
                                        color: '#fff',
                                        fontSize: 18,
                                        borderRadius: 5,
                                        border: 'none',
                                        opacity: (isGeneratingPdf || status?.status === 'generating') ? 0.7 : 1
                                    }}
                                    onClick={handleGeneratePdf}
                                >
                                    <strong>
                                        <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: 8 }} />
                                        {(isGeneratingPdf || status?.status === 'generating') ? 'Generando PDF...' : 'Descargar PDF'}
                                    </strong>
                                </button>
                            </div>
                        </Grid>
                    )}

                    {(['En proceso', 'Por cerrar', 'Completadas', 'Reasignar', 'Asignar'].includes(state)) && (
                        <>
                            <Grid item xs>
                                <div style={{ padding: 10 }}>
                                    <button
                                        disabled={!(totalBacklogs > 0)}
                                        style={{
                                            cursor: !(totalBacklogs > 0) ? 'not-allowed' : 'pointer',
                                            height: 50,
                                            width: '100%',
                                            backgroundColor: !(totalBacklogs > 0) ? '#ccc' : '#F2994A',
                                            color: '#fff',
                                            fontSize: 14,
                                            borderRadius: 5,
                                            border: 'none'
                                        }}
                                        onClick={() => {
                                            setPreviewType('findings')
                                            setPreviewOpen(true)
                                        }}
                                        title={!(totalBacklogs > 0) ? 'No hay hallazgos para mostrar' : 'Ver vista previa del resumen de hallazgos'}
                                    >
                                        <strong> <FontAwesomeIcon icon={faEye} /> Reporte resumen de hallazgos</strong>
                                    </button>
                                </div>
                            </Grid>
                            <Grid item xs>
                                <div style={{ padding: 10 }}>
                                    <button
                                        disabled={!(totalNoChecks > 0)}
                                        style={{
                                            cursor: !(totalNoChecks > 0) ? 'not-allowed' : 'pointer',
                                            height: 50,
                                            width: '100%',
                                            backgroundColor: !(totalNoChecks > 0) ? '#ccc' : '#E67E22',
                                            color: '#fff',
                                            fontSize: 18,
                                            borderRadius: 5,
                                            border: 'none'
                                        }}
                                        onClick={() => {
                                            setPreviewType('no-checks')
                                            setPreviewOpen(true)
                                        }}
                                        title={!(totalNoChecks > 0) ? 'No hay actividades marcadas como no ejecutadas' : 'Ver vista previa de actividades no ejecutadas'}
                                    >
                                        <strong> <FontAwesomeIcon icon={faEye} /> Tareas no ejecutadas</strong>
                                    </button>
                                </div>
                            </Grid>
                        </>
                    )}

                    {(admin || isSuperAdmin) && <Grid item xs>
                        <div style={{ padding: 10 }}>
                            <button style={{ cursor: 'pointer', height: 50, width: '100%', backgroundColor: 'red', color: '#fff', fontSize: 18, borderRadius: 5, border: 'none' }} onClick={handleSoftDeleteOt}> <strong> <FontAwesomeIcon icon={faTrash} /> Eliminar OT {idIndex} </strong> </button>
                        </div>
                    </Grid>}
                </Grid>
                <Fab onClick={onlyClose} style={{ position: 'absolute', right: 10, top: 10, boxShadow: 'none', backgroundColor: 'transparent' }}>
                    <Close style={{ color: '#ccc' }} />
                </Fab>

            </div>
        </Dialog>
    )
}

export default ReviewReportDialog
